'use client';

import { Cross2Icon } from '@radix-ui/react-icons';
import { Command as CommandPrimitive } from 'cmdk';
import * as React from 'react';

import { Badge } from '../../components/badge';
import { Button } from '../../components/button';
import { Command, CommandGroup, CommandItem, CommandList } from '../../components/command';
import { LoadingIcon } from '../../components/loadingIcon';
import { ScrollArea } from '../../components/scrollArea';
import { cn } from '../../utils';
import { useEvent, useLatest } from '../../utils/hooks';

type SelectItem = Record<'value' | 'label', string>;

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref != null) {
    (ref as React.MutableRefObject<T | null>).current = value;
  }
}

export interface MultipleSelectProps {
  options: SelectItem[];
  onChange?: (val: string[]) => void;
  placeholder?: string;
  selected?: string[];
  loading?: boolean;
  disabled?: boolean;
  id?: string;
  /** When true, shows error styling and sets aria-invalid on the input. */
  error?: boolean;
  /** When true, removes the focus-within ring on the wrapper. Default: false (existing behavior). */
  hideFocusRing?: boolean;
  /** Minimum number of selections required before the inline "Clear all" button renders. Default: 0 (always shown). */
  clearAllMinSelections?: number;
  /** Extra classes for the dropdown list, e.g. to raise the default max-h-40 cap. */
  listClassName?: string;
  'aria-describedby'?: string;
  'aria-required'?: boolean | 'true' | 'false';
  'aria-invalid'?: boolean | 'false' | 'true' | 'grammar' | 'spelling';
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const staticFn = () => {};

export const MultipleSelect = React.forwardRef<HTMLInputElement, MultipleSelectProps>(
  function MultipleSelect(
    {
      options,
      placeholder = '',
      loading,
      selected: selectedProp,
      onChange = staticFn,
      disabled,
      id,
      error,
      hideFocusRing = false,
      clearAllMinSelections = 0,
      listClassName,
      'aria-describedby': ariaDescribedBy,
      'aria-required': ariaRequired,
      'aria-invalid': ariaInvalidProp,
    },
    ref,
  ) {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const setInputRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node;
        assignRef(ref, node);
      },
      [ref],
    );
    const [open, setOpen] = React.useState(false);

    const [selected, _setSelected] = React.useState<SelectItem[]>(() => {
      return options.filter((o) => selectedProp?.includes(o.value));
    });
    const selectedValue = useLatest(selected);
    const [inputValue, setInputValue] = React.useState('');

    React.useEffect(() => {
      if (
        selectedProp &&
        selectedValue.current.map((s) => s.value).join(',') !== selectedProp.join(',')
      ) {
        _setSelected(options.filter((o) => selectedProp.includes(o.value)));
      }
    }, [options, selectedProp, selectedValue]);

    const onChangeEvent = useEvent(onChange);

    const setSelected: React.Dispatch<React.SetStateAction<SelectItem[]>> = React.useCallback(
      (updater) => {
        _setSelected((prev) => {
          const newValue = typeof updater === 'function' ? updater(prev) : updater;
          onChangeEvent?.(newValue.map((s) => s.value));
          return newValue;
        });
      },
      [onChangeEvent],
    );

    const handleUnselect = React.useCallback(
      (selectedItem: SelectItem) => {
        setSelected((prev) => prev.filter((s) => s.value !== selectedItem.value));
      },
      [setSelected],
    );

    const selectables = (options || []).filter((item) => !selectedProp?.includes(item.value));

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        const input = inputRef.current;
        if (input) {
          if (e.key === 'Delete' || e.key === 'Backspace') {
            if (input.value === '') {
              setSelected((prev) => {
                const newSelected = [...prev];
                newSelected.pop();
                return newSelected;
              });
            }
          }
          if (e.key === 'Escape') {
            input.blur();
            e.stopPropagation();
          }
        }
      },
      [setSelected],
    );

    // Prevent dropdown from closing when user clicks inside the content area
    const handleMouseDown = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const ariaInvalid = ariaInvalidProp !== undefined ? ariaInvalidProp : error ? true : undefined;

    return (
      <Command onKeyDown={handleKeyDown} className="overflow-visible bg-transparent">
        <div
          className={cn(
            'border-input ring-offset-background group rounded-md border px-3 py-2 text-sm',
            !hideFocusRing &&
              'focus-within:ring-ring focus-within:ring-2 focus-within:ring-offset-2',
            error && 'border-destructive',
            error && !hideFocusRing && 'focus-within:ring-destructive',
          )}
        >
          <div className="flex flex-wrap gap-1">
            {selected.map((framework) => {
              return (
                <Badge key={framework.value} variant="secondary">
                  {framework.label}
                  <button
                    className="ring-offset-background focus:ring-ring ml-1 rounded-full outline-none focus:ring-2 focus:ring-offset-2"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleUnselect(framework);
                      }
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    disabled={disabled}
                    onClick={() => handleUnselect(framework)}
                  >
                    {!disabled && <Cross2Icon />}
                  </button>
                </Badge>
              );
            })}
            {loading && <LoadingIcon />}
            <CommandPrimitive.Input
              id={id}
              disabled={disabled || selectables.length === 0}
              ref={setInputRef}
              value={inputValue}
              onValueChange={setInputValue}
              onBlur={() => setOpen(false)}
              onFocus={() => setOpen(true)}
              placeholder={selectables.length > 0 ? placeholder : undefined}
              className="placeholder:text-muted-foreground ml-2 flex-1 bg-transparent outline-none"
              aria-invalid={ariaInvalid}
              aria-describedby={ariaDescribedBy}
              aria-required={ariaRequired}
            />
            {selected.length >= clearAllMinSelections && (
              <Button
                type="button"
                disabled={disabled}
                onClick={() => {
                  setSelected([]);
                }}
                variant="outline"
                size="sm"
                className="h-6"
              >
                Clear all
              </Button>
            )}
          </div>
        </div>
        {open && selectables.length > 0 && (
          <div className="relative">
            <div
              className="bg-popover text-popover-foreground animate-in absolute top-0 z-50 w-full rounded-md border shadow-md outline-none"
              onMouseDown={handleMouseDown}
            >
              <CommandList className={cn('max-h-40', listClassName)}>
                <ScrollArea className="h-full">
                  <CommandGroup className="h-full">
                    {selectables.map((selectable) => {
                      return (
                        <CommandItem
                          key={selectable.value}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onSelect={() => {
                            setInputValue('');
                            setSelected((prev) => [...prev, selectable]);
                          }}
                          className={'cursor-pointer'}
                        >
                          {selectable.label}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </ScrollArea>
              </CommandList>
            </div>
          </div>
        )}
      </Command>
    );
  },
);

MultipleSelect.displayName = 'MultipleSelect';
